import React, { useEffect, useState } from 'react';
import { Table, Radio, Input, Row } from 'antd';
import { ColumnGroupType, ColumnType } from 'antd/es/table';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { useTranslation } from 'react-i18next';
import { GRAM_OPTIONS, POUND_OPTIONS } from '../../../enums/ScaleVerificationWeightsEnum';
import DefaultButton from '../../../components/buttons/DefaultButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import EccentricityTest from './EccentricityReference';

type RowType = {
  key: number;
  grams: string;
  pounds: string;
};

type RowState = {
  repeatability?: number;
  ascending?: string;
  descending?: string;
};

interface TableProps {
  submit?: (data: Record<number, RowState>) => void;
  initialValues?: Record<number, RowState>;
  isViewMode?: boolean;
  handleBack?: () => void;
  handleCancel: () => void;
  onValidationError?: (message: string) => void;
}

const ScaleVerificationTable: React.FC<TableProps> = ({
  submit,
  initialValues = {},
  isViewMode = false,
  handleBack,
  handleCancel,
  onValidationError,
}) => {
  const [rowValues, setRowValues] = useState<Record<number, RowState>>(
    initialValues && initialValues
  );
  const { t } = useTranslation('price');

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setRowValues(initialValues);
    }
  }, [initialValues]);

  const rows: RowType[] = GRAM_OPTIONS.map((gram, index) => ({
    key: index + 1,
    grams: gram,
    pounds: POUND_OPTIONS[index],
  }));

  const updateRow = (key: number, updates: Partial<RowState>) => {
    setRowValues((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...updates,
      },
    }));
  };

  const handleRepeatabilityRadio = (key: number, value: number) => {
    setRowValues((prev) => {
      const current = prev[key]?.repeatability;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          repeatability: current === value ? undefined : value,
        },
      };
    });
  };

  const columns: (ColumnType<RowType> | ColumnGroupType<RowType>)[] = [
    {
      title: 'No',
      dataIndex: 'key',
      width: 60,
      align: 'center',
    },
    {
      title: 'Grams / Gramos',
      dataIndex: 'grams',
      align: 'center',
    },
    {
      title: 'Pounds / Libras',
      dataIndex: 'pounds',
      align: 'center',
    },
    {
      title: t('linearityTest'),
      children: [
        {
          title: t('ascending'),
          align: 'center',
          render: (_: any, record: RowType) => (
            <Input
              variant="borderless"
              className="m-0 p-0"
              value={rowValues[record.key]?.ascending}
              onChange={(e) => updateRow(record.key, { ascending: e.target.value })}
              placeholder={!isViewMode ? t('ascendingValue') : ' - '}
              disabled={isViewMode}
            />
          ),
        },
        {
          title: t('descending'),
          align: 'center',
          render: (_: any, record: RowType) => (
            <Input
              variant="borderless"
              className="m-0 p-0"
              value={rowValues[record.key]?.descending}
              onChange={(e) => updateRow(record.key, { descending: e.target.value })}
              placeholder={!isViewMode ? t('descendingValue') : ' - '}
              disabled={isViewMode}
            />
          ),
        },
      ],
    },
    {
      title: t('repeatabilityTest'),
      children: [
        {
          title: '1',
          align: 'center',
          render: (_: any, record: RowType) => (
            <Radio
              value={1}
              checked={rowValues[record.key]?.repeatability === 1}
              onClick={() => handleRepeatabilityRadio(record.key, 1)}
              disabled={isViewMode}
            />
          ),
        },
        {
          title: '2',
          align: 'center',
          render: (_: any, record: RowType) => (
            <Radio
              value={2}
              checked={rowValues[record.key]?.repeatability === 2}
              onClick={() => handleRepeatabilityRadio(record.key, 2)}
              disabled={isViewMode}
            />
          ),
        },
        {
          title: '3',
          align: 'center',
          render: (_: any, record: RowType) => (
            <Radio
              value={3}
              checked={rowValues[record.key]?.repeatability === 3}
              onClick={() => handleRepeatabilityRadio(record.key, 3)}
              disabled={isViewMode}
            />
          ),
        },
      ],
    },
  ];

  const validateTableEntries = () => {
    const filledRows = Object.keys(rowValues).filter((key: any) => {
      const row = rowValues[key];
      return (
        (row?.ascending && row.ascending !== '') ||
        (row?.descending && row.descending !== '') ||
        row?.repeatability !== undefined
      );
    });

    // Check if at least one row is filled
    if (filledRows.length === 0) {
      onValidationError?.(t('atLeastOneEntryIsRequired'));
      return false;
    }

    for (const rowKey of filledRows) {
      const row = rowValues[Number(rowKey)];

      // Check if at least one of ascending or descending is filled
      const hasLinearityValue =
        (row?.ascending && row.ascending !== '') || (row?.descending && row.descending !== '');

      if (!hasLinearityValue) {
        onValidationError?.(t('ascendingOrDescendingRequired'));
        return false;
      }

      // Check if repeatability is selected
      if (row?.repeatability === undefined || row?.repeatability === null) {
        onValidationError?.(t('repeatabilitySelectionRequired'));
        return false;
      }
    }

    return true;
  };

  return (
    <div className="m-0 w-full p-4">
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('inspection:scaleFormatVerification')}
        </h2>
      </div>
      <EccentricityTest />
      <span className="p-3"></span>
      <Table rowKey="key" dataSource={rows} columns={columns} pagination={false} bordered />
      {!isViewMode && (
        <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row">
          <DefaultButton handleOnClick={handleCancel} width="100%">
            {t('cancel')}
          </DefaultButton>
          <SecondaryButton
            handleOnClick={() => {
              handleBack?.();
            }}
            width="100%"
          >
            {t('goBack')}
          </SecondaryButton>
          <PrimaryButton
            handleOnClick={() => {
              if (validateTableEntries()) {
                submit ? submit(rowValues) : () => {};
              }
            }}
            width="100%"
            hideBgGradient={true}
          >
            {t('goNext')}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default ScaleVerificationTable;
